import { useEffect, useState, Dispatch } from "react";
import { UseFormReturn } from "react-hook-form";

import { getPrefills } from "api";
import errorToast from "helpers/errorToast";
import { groupByKeys } from "helpers/commonHelpers";
import usePermissions from "hooks/usePermissions";
import { RequestFormActionType, RequestFormAction } from "./RequestFormState";

interface IPrefill {
  id: number;
  input_field: string;
  input_field_label: string;
  input_value: string;
  input_value_label: string;
  output_field: string;
  output_field_label: string;
  output_value: string;
  output_value_label: string;
  disabled: boolean;
  location: {
    key: string;
    label: string;
  };
  created_by: string;
}

const extractOutputFields = (data: any) => {
  const outputFields: string[] = [];
  Object.keys(data).forEach((key) => {
    if (Array.isArray(data[key]) && data[key].length > 0 && data[key][0].output_field) {
      data[key].forEach((item: { output_field: string }) => {
        outputFields.push(item.output_field);
      });
    }
  });
  return outputFields;
};

function useFormPrefill(methods: UseFormReturn<any, object>, dispatch: Dispatch<RequestFormAction>) {
  const { watch, getValues, setValue, trigger } = methods;
  const { permissions }: any = usePermissions();
  const { enable_new_address_structure } = JSON.parse(permissions || "{}").company_shipper_settings;
  const location = watch("organizational.location");

  const [preFillValues, setPreFillValues] = useState<{ [key: string]: IPrefill[] }>();

  useEffect(() => {
    if (!location) return;
    // If there are prefill values already, clear the state values for the output fields. This is necessary when changing location.
    if (preFillValues) {
      const previousOutputFields = extractOutputFields(preFillValues);
      previousOutputFields.forEach((field) => {
        clearStateValues(field);
      });
    }
    getPrefills({
      params: {
        location,
        page_size: 1000,
      },
    })
      .then((res: any) => {
        const objectByKeys: any = groupByKeys(res.data.results, "input_field");
        setPreFillValues(objectByKeys);
      })
      .catch((err: any) => errorToast(err));
  }, [location]);

  useEffect(() => {
    if (!preFillValues) return;
    updatePrefillReset();
    const subscription = watch((value, { name }) => {
      if (!name || !preFillValues[name]) return;
      updatePrefills(name);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [preFillValues]);

  // Used when updating disabled fields after the form used a methods.reset to fill out the values.
  const updatePrefillReset = () => {
    if (!preFillValues) return;
    Object.keys(preFillValues).forEach((name) => {
      updatePrefills(name);
    });
  };

  // Updates disabled fields and their tooltips if a prefill value is matched.
  const updatePrefills = (name: string) => {
    if (!preFillValues) return;
    const isInvitedModes = name.includes("invited_modes");
    const inputFieldValue = getValues(name);

    const matchingValues = preFillValues[name].filter(
      (item) =>
        (isInvitedModes && inputFieldValue === true) ||
        item.input_value === inputFieldValue ||
        item.input_value_label === inputFieldValue,
    );

    if (matchingValues && matchingValues.length > 0) {
      matchingValues.forEach((matchingValue) => {
        const {
          output_field,
          output_value,
          output_value_label,
          disabled,
          input_field_label,
          input_value_label,
          input_value,
        } = matchingValue;

        // if new address structure is enabled we skip prefilling address fields
        if (
          enable_new_address_structure &&
          (output_field === "schedule.pickup_address_name" || output_field === "schedule.stop1_address_name")
        ) {
          return;
        }

        if (
          (isInvitedModes && inputFieldValue === true) ||
          matchingValue.input_value === inputFieldValue ||
          matchingValue.input_value_label === inputFieldValue
        ) {
          // Set the output field to the matching value found in the prefills. Countries needed specific formtting due to having both the country and country code.

          if (output_field === "schedule.pickup_country" || output_field === "schedule.stop1_country") {
            setValue(output_field, output_value_label);
            setValue(`${output_field}_code`, output_value);
          } else {
            setValue(output_field, output_value);
          }

          trigger(output_field);

          // Update disabled fields
          dispatch({
            type: RequestFormActionType.UPDATE_DISABLED_FIELDS,
            payload: {
              key: output_field,
              value: disabled,
            },
          });

          // Add info message for disabled fields
          if (disabled) {
            dispatch({
              type: RequestFormActionType.UPDATE_TOOLTIP_FIELDS,
              payload: {
                key: output_field,
                value: disabled ? `Field disabled because ${input_field_label} is ${input_value_label || input_value}.` : "",
              },
            });
          }
        }
      });
    } else if (matchingValues === undefined || matchingValues.length === 0) {
      preFillValues[name].forEach((item) => {
        clearStateValues(item.output_field);
      });
    }
  };

  // Clears the disabled field state and tooltip
  const clearStateValues = (field: string) => {
    dispatch({
      type: RequestFormActionType.UPDATE_DISABLED_FIELDS,
      payload: {
        key: field,
        value: false,
      },
    });
    dispatch({
      type: RequestFormActionType.UPDATE_TOOLTIP_FIELDS,
      payload: {
        key: field,
        value: "",
      },
    });
  };
  return { updatePrefillReset };
}

export default useFormPrefill;
